import {
  ListSubheader,
  MenuItem as MaterialMenuItem,
  TextField,
} from '@material-ui/core'
import MaterialSelect from '@material-ui/core/Select'
import { makeStyles } from '@material-ui/core/styles'
import * as d3 from 'd3-hierarchy'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import MenuItem, { IValueType } from './MenuItem'
import useValidateItems from './hooks/useValidateItems'
import Select from './index'

interface ISearchableSelectProps<Value extends IValueType> {
  value: Value['value'][]
  onChange: (value: Value['value'][]) => void
  dataSource: Value[]
  searchable?: boolean
  className?: string
  fullWidth?: boolean
  displayEmpty?: boolean
  disabled?: boolean
}

const useStyles = makeStyles({
  text: {
    fontFamily: 'Lexend-Regular',
  },
  listSubheader: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: 'white',
  },
})

const SearchablePart = <Value extends IValueType>({
  className = '',
  value,
  onChange,
  dataSource,
  fullWidth,
  displayEmpty,
  disabled,
}: Omit<ISearchableSelectProps<Value>, 'searchable'>) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const option = useMemo(() => d3.stratify<Value>()(dataSource), [dataSource])
  const node = useValidateItems({ value, onChange, option })

  const handleClose = useCallback(() => {
    setOpen(false)
    setSearchText('')
  }, [])

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
    }
  }, [open])

  const flatOptions = useMemo(() => {
    const options: { text: string; value: Value['value'][] }[] = []
    option.children?.forEach((category) => {
      category.children?.forEach((item) => {
        options.push({
          text: `${category.data.text} / ${item.data.text}`,
          value: [category.data.value, item.data.value],
        })
      })
    })
    return options
  }, [option])

  const filteredOptions = useMemo(() => {
    if (!searchText) return []
    return flatOptions.filter((opt) =>
      opt.text.split(' / ')[1].toLowerCase().includes(searchText.toLowerCase()),
    )
  }, [searchText, flatOptions])

  return (
    <MaterialSelect
      className={`${classes.text} ${className}`}
      value={value.join('/')}
      renderValue={() => node.map(({ text }) => text).join(' / ')}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={handleClose}
      variant="outlined"
      fullWidth={fullWidth}
      displayEmpty={displayEmpty}
      disabled={disabled}
      MenuProps={{
        autoFocus: false,
        onClose: (event: React.FocusEvent<HTMLInputElement>) => {
          if (event.target?.id !== 'search-input') {
            handleClose()
          }
        },
      }}
    >
      <ListSubheader className={classes.listSubheader}>
        <TextField
          id="search-input"
          inputRef={searchInputRef}
          size="medium"
          placeholder="Search item..."
          fullWidth
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </ListSubheader>
      {searchText ? (
        filteredOptions.length > 0 ? (
          filteredOptions.map((opt) => (
            <MaterialMenuItem
              key={opt.value.join('-')}
              value={opt.value.join('/')}
              onClick={() => {
                onChange(opt.value)
                handleClose()
              }}
              className={classes.text}
            >
              {opt.text}
            </MaterialMenuItem>
          ))
        ) : (
          <MaterialMenuItem disabled className={classes.text}>
            No items found
          </MaterialMenuItem>
        )
      ) : (
        option?.children?.map((subOption) => (
          <MenuItem<Value>
            key={subOption.id}
            onChange={onChange}
            onClose={handleClose}
            option={subOption}
          />
        ))
      )}
    </MaterialSelect>
  )
}

// This is the main component that decides which version to render.
const SearchableSelect = <Value extends IValueType>(
  props: ISearchableSelectProps<Value>,
) => {
  if (props.searchable) {
    return <SearchablePart {...props} />
  }

  return <Select {...props} />
}

export default React.memo(
  SearchableSelect,
) as unknown as typeof SearchableSelect
